import * as THREE from "three";
import { OPPOSITE_SIDES, STANDARD_MODULE_PANEL_SLOTS } from "../../utils/constants.js";
import { COMPONENT_ACROSS_AXIS } from "../AttachmentPoint.js";

// Scratch instances for hasFlankingCushions (runs per-frame during component
// drag via the snap query) — avoids per-call THREE allocations.
const _flankApPos = new THREE.Vector3();
const _flankApQuat = new THREE.Quaternion();
const _flankRightAxis = new THREE.Vector3();
const _flankToCushion = new THREE.Vector3();

// Scratch instances for the requiresAdjacentModuleOnSides corner gate.
const _spanApLocal = new THREE.Vector3();
const _spanOtherLocal = new THREE.Vector3();
const _spanTangent = new THREE.Vector3();
const _spanAxisX = new THREE.Vector3();
const _spanAxisZ = new THREE.Vector3();
const _spanQuat = new THREE.Quaternion();
const _spanMyQuat = new THREE.Quaternion();
// _groupModuleOnSide scratch — it runs per drag frame.
const _adjWorldDir = new THREE.Vector3();
const _adjToOther = new THREE.Vector3();

// Local outward direction of each cardinal side.
const LOCAL_SIDE_DIR = {
  left:  new THREE.Vector3(-1, 0, 0),
  right: new THREE.Vector3( 1, 0, 0),
  front: new THREE.Vector3( 0, 0, 1),
  back:  new THREE.Vector3( 0, 0,-1),
};

// Slack (m) when testing whether a module seated on a face reaches an attachment
// point along that face. Absorbs the inset of a pillow slot from the corner.
const MODULE_SPAN_MARGIN = 0.15;
// How far (m) a module's near edge may be from a face plane and still count as
// seated against it. Snapped modules meet flush, so this only absorbs authored
// collision-box vs. model rounding.
const MODULE_FACE_TOLERANCE = 0.12;

// Module adjacency, group walks and panel placement rules; installed on FurnitureElement.prototype.
export const moduleAdjacencyMethods = {
  /**
   * Determines if an attachment point should be visible based on business rules
   * @param {AttachmentPoint} point - The attachment point to check
   * @returns {boolean} - True if the point should be shown
   */
  shouldShowAttachmentPoint(point) {
    const isModule = this.furnitureData?.componentType === "module";

    // CRITICAL: For modules, NEVER show attachment points that accept other modules
    // Module-to-module connections are handled via drag-and-snap, NOT visible attachment points
    // Only components (panels, backrests, cushions) use visible attachment points
    if (isModule && point.acceptsComponentTypes.includes("module")) {
      return false;
    }

    // Only show this AP when this furniture is attached to one of the required parents
    if (point.requiresParent?.length > 0) {
      const parentUrlCode = this.attachedTo?.parentFurniture?.furnitureData?.urlCode;
      if (!point.requiresParent.includes(parentUrlCode)) return false;
    }

    // If any module in the connected group is adjacent to this side, hide panel APs
    if (isModule && this.hasAdjacentModuleOnSide(point.side)) {
      return false;
    }

    // Check panel attachment restrictions (max 2 adjacent panels per module)
    if (point.acceptsComponentTypes.includes("panel") && !this.canAttachPanel(point, "panel")) {
      return false;
    }

    // If the AP requires a specific directly adjacent module, only show when one is touching.
    if (!this.hasRequiredAdjacentModule(point)) {
      return false;
    }

    // If the AP requires two flanking cushions (e.g. a diagon pillow that may
    // only be placed when a big cushion sits on each side of the slot), only
    // show it when both flanks are present.
    if (point.requiresFlankingCushions?.length > 0 && !this.hasFlankingCushions(point)) {
      return false;
    }

    return true;
  },

  /**
   * Walk up through module-to-module connections to the connected group's root
   * module. Stops at the first non-module parent (panels/backrests/cushions are
   * not part of the module chain).
   */
  findGroupRoot() {
    let root = this;
    while (root.attachedTo) {
      const parent = root.attachedTo.parentFurniture;
      if (parent?.furnitureData?.componentType === "module") {
        root = parent;
      } else {
        break;
      }
    }
    return root;
  },

  /** This element if it's a module, else the first module up its attachment chain. */
  getNearestModule() {
    let f = this;
    while (f && f.furnitureData?.componentType !== "module") f = f.attachedTo?.parentFurniture ?? null;
    return f;
  },

  /**
   * Collect all modules in the connected furniture group
   */
  getAllGroupModules() {
    const modules = new Set();
    const root = this.findGroupRoot();

    // Traverse down from root collecting all modules
    const traverse = (element) => {
      if (element.furnitureData?.componentType === "module") {
        modules.add(element);
      }
      for (const child of element.attachedChildren) {
        if (child.furnitureData?.componentType === "module") {
          traverse(child);
        }
      }
    };
    traverse(root);
    return modules;
  },

  /**
   * Collect every cushion in the connected group whose urlCode is in `urlCodes`.
   * Unlike getAllGroupModules(), this descends through ALL attached children
   * (modules → backrests → cushions), since flanking cushions sit on backrests
   * belonging to different parents than the AP being gated.
   * @param {string[]} urlCodes
   * @returns {FurnitureElement[]}
   */
  getAllGroupCushions(urlCodes) {
    const root = this.findGroupRoot();

    const cushions = [];
    const seen = new Set();
    const traverse = (element) => {
      if (seen.has(element)) return;
      seen.add(element);
      if (
        element.furnitureData?.componentType === "cushion" &&
        urlCodes.includes(element.furnitureData?.urlCode)
      ) {
        cushions.push(element);
      }
      for (const child of element.attachedChildren) traverse(child);
    };
    traverse(root);
    return cushions;
  },

  /**
   * True when a cushion matching `point.requiresFlankingCushions` sits on BOTH
   * sides of this attachment point — i.e. the slot is surrounded. Used for
   * angled pillows (diagon-mp / diagon-sp) that may only be placed between two
   * big cushions (diagon-bp).
   *
   * The flanking cushions live on separate backrests (different parents), so the
   * test is world-space, mirroring the geometric fallback in
   * hasAdjacentModuleOnSide(): project each nearby cushion onto the AP's local
   * "right" axis and require at least one with a positive and one with a
   * negative projection. The sign convention is irrelevant — we only need one
   * cushion on each opposing side.
   */
  hasFlankingCushions(point) {
    const urlCodes = point.requiresFlankingCushions;
    if (!urlCodes?.length) return false;

    const candidates = this.getAllGroupCushions(urlCodes);
    if (candidates.length < 2) return false;

    const apPos = point.getWorldPosition(_flankApPos);
    // The seated component's across-the-face axis in world space — the axis the cushions flank along.
    point.getAttachmentOrientation(_flankApQuat);
    const rightAxis = _flankRightAxis.copy(COMPONENT_ACROSS_AXIS).applyQuaternion(_flankApQuat);
    rightAxis.y = 0;
    if (rightAxis.lengthSq() < 1e-6) return false;
    rightAxis.normalize();

    // flankRadius is always set at load (defaulted to 0.6 in the constructor).
    const radiusSq = point.flankRadius * point.flankRadius;

    let hasPositive = false;
    let hasNegative = false;
    for (const cushion of candidates) {
      cushion.getMesh().getWorldPosition(_flankToCushion);
      _flankToCushion.sub(apPos);
      _flankToCushion.y = 0;
      if (_flankToCushion.lengthSq() > radiusSq) continue;
      const proj = _flankToCushion.dot(rightAxis);
      if (proj > 0.01) hasPositive = true;
      else if (proj < -0.01) hasNegative = true;
      if (hasPositive && hasNegative) return true;
    }
    return false;
  },

  /**
   * Checks if any connected module is adjacent on the specified side.
   *
   * Uses attachment relationship data as the primary check (reliable for all snap
   * positions), falling back to geometry only for indirect group connections
   * (e.g. sibling modules in a 3+ module chain).
   *
   * FIX: The geometric fallback previously used a hardcoded 4.0 m distance cap.
   * For larger or L-shaped modules the centre-to-centre distance between connected
   * siblings can legitimately exceed 4 m, causing the fallback to silently skip
   * valid neighbours and incorrectly show panel APs on occupied sides.
   * The cap is now computed from each module's bounding box so it scales with
   * module size and works correctly for all module types.
   */
  hasAdjacentModuleOnSide(side) {
    // Primary: use attachment data directly — works regardless of snap position.
    // Case 1: this module is the child. Use moduleConnectionSide — the local side
    //         of THIS module whose AP physically connects to the parent. Set by
    //         attachTo() so it's available before AP occupancy is updated.
    //         (OPPOSITE_SIDES[parent.AP.side] only works when rotations match.)
    if (this.attachedTo) {
      const parentFurniture = this.attachedTo.parentFurniture;
      if (parentFurniture?.furnitureData?.componentType === "module") {
        if (this.moduleConnectionSide) {
          if (this.moduleConnectionSide === side) return true;
        } else {
          // Fallback when moduleConnectionSide not yet set (e.g. after state restore):
          // infer from parent AP side — valid only when rotations match
          if (OPPOSITE_SIDES[this.attachedTo.side] === side) return true;
        }
      }
    }

    // Case 2: this module is the parent. A child's attachedTo.side indicates
    //         which of THIS module's sides the child is connected on.
    for (const child of this.attachedChildren) {
      if (child.furnitureData?.componentType !== "module") continue;
      if (child.attachedTo?.side === side) return true;
    }

    // Fallback: geometric check for indirect connections in groups of 3+
    // (e.g. B and C are both children of A — B needs to know C is adjacent)
    return this._groupModuleOnSide(side);
  },

  /**
   * True when an AP's `requiresAdjacentModule` gate is satisfied.
   *
   * Three flavours, most specific first:
   *   • `requiresAdjacentModuleOnSides` (array) — every listed side must carry a
   *     required module that actually reaches the AP. Corner pillow slots on bard
   *     E elements use this: the slot only exists where two R backrests meet.
   *   • `requiresAdjacentModuleOnSide` (string) — one specific side must carry it.
   *   • neither — any directly adjacent module in the list qualifies.
   */
  hasRequiredAdjacentModule(point) {
    const required = point.requiresAdjacentModule;
    if (!required?.length) return true;

    const sides = point.requiresAdjacentModuleOnSides;
    if (sides?.length > 0) {
      return sides.every(side => this._hasSpanningModuleOnSide(side, required, point));
    }

    if (point.requiresAdjacentModuleOnSide) {
      const side = point.requiresAdjacentModuleOnSide;
      return this._hasRequiredModuleOnSide(side, required)
          || this._hasSpanningModuleOnSide(side, required, point);
    }

    const directNeighbors = this.attachedChildren.filter(
      c => c.furnitureData?.componentType === "module"
    );
    const directParent = this.attachedTo?.parentFurniture;
    if (directParent?.furnitureData?.componentType === "module") {
      directNeighbors.push(directParent);
    }
    return directNeighbors.some(
      m => required.includes(m.furnitureData?.urlCode)
    );
  },

  /**
   * Candidate neighbours for the geometric adjacency tests: every module in the
   * scene, not just this one's attachment group.
   *
   * The group is the wrong set here. Dragging a module detaches all of its module
   * children (EventManager.onMouseMove) and nothing re-links them on drop, so the
   * attachment graph degrades while the furniture itself stays put — move one seat
   * of a bard run and the backrest chain hanging off it becomes its own group.
   * Gated on the graph, the pillows on those untouched backrests (and the corner
   * pillows on the seat next door) get culled by removeUnsupportedFragileCushions.
   * Physical adjacency is what the gate means, so widen the set and let the strict
   * seated-against-the-face test below decide.
   */
  _adjacencyCandidateModules() {
    const modules = this.getAllGroupModules();
    const root = this.scene ?? this.mesh?.parent;
    if (!root) return modules;
    for (const child of root.children) {
      const el = child.userData?.furnitureElement;
      if (el?.furnitureData?.componentType === "module") modules.add(el);
    }
    return modules;
  },

  /**
   * True when a module matching `urlCodes` is seated against this module's `side`
   * face AND reaches `point` laterally along that face.
   *
   * Both halves are needed for corner pillow slots. A backrest that borders the
   * same side somewhere is not enough (bard-r1 is 0.88 m against bard-e3's 2.008 m,
   * so it can sit mid-flank and never reach the corner), and a backrest belonging
   * to the *neighbouring* seat can line up with this face's plane while sitting well
   * past the corner. Attachment relationships can't answer either question, so the
   * test is geometric: everything is projected into this module's local frame, with
   * each candidate's extents resolved along the face normal and tangent via its own
   * axes — a backrest running perpendicular to the face presents its 0.25 m depth
   * there, not its length.
   */
  _hasSpanningModuleOnSide(side, urlCodes, point) {
    const normal = LOCAL_SIDE_DIR[side];
    const mySize = this.furnitureData?.properties?.collisionBox?.size;
    const myMesh = this.getMesh();
    if (!normal || !mySize || !myMesh) return false;

    myMesh.updateMatrixWorld(true);
    myMesh.getWorldQuaternion(_spanMyQuat).invert();

    // Face tangent = normal turned 90° about Y.
    const tangent = _spanTangent.set(-normal.z, 0, normal.x);
    const myHalfN = Math.abs(normal.x) * mySize.x / 2 + Math.abs(normal.z) * mySize.z / 2;

    point.getWorldPosition(_spanApLocal);
    myMesh.worldToLocal(_spanApLocal);
    const apLat = _spanApLocal.dot(tangent);

    for (const other of this._adjacencyCandidateModules()) {
      if (other === this) continue;
      if (!urlCodes.includes(other.furnitureData?.urlCode)) continue;

      const otherSize = other.furnitureData?.properties?.collisionBox?.size;
      const otherMesh = other.getMesh();
      if (!otherSize || !otherMesh) continue;

      otherMesh.getWorldPosition(_spanOtherLocal);
      myMesh.worldToLocal(_spanOtherLocal);

      // Candidate's own axes, expressed in this module's local frame.
      otherMesh.getWorldQuaternion(_spanQuat).premultiply(_spanMyQuat);
      const otherX = _spanAxisX.set(1, 0, 0).applyQuaternion(_spanQuat);
      const otherZ = _spanAxisZ.set(0, 0, 1).applyQuaternion(_spanQuat);

      const halfN = Math.abs(normal.dot(otherX)) * otherSize.x / 2
                  + Math.abs(normal.dot(otherZ)) * otherSize.z / 2;

      // Seated against this face: its near edge meets the face plane.
      if (Math.abs(_spanOtherLocal.dot(normal) - myHalfN - halfN) > MODULE_FACE_TOLERANCE) continue;

      const halfT = Math.abs(tangent.dot(otherX)) * otherSize.x / 2
                  + Math.abs(tangent.dot(otherZ)) * otherSize.z / 2;

      // …and it reaches the slot along the face.
      if (Math.abs(apLat - _spanOtherLocal.dot(tangent)) <= halfT + MODULE_SPAN_MARGIN) return true;
    }

    return false;
  },

  /**
   * Check if a required adjacent module (by urlCode) is connected on a specific side.
   * Used by cushion APs on R modules to show only when an E module is on the correct side.
   */
  _hasRequiredModuleOnSide(side, requiredUrlCodes) {
    // Case 1: this module is the child — check if parent is on the specified side.
    // moduleConnectionSide is the side of THIS module that connects to the parent,
    // so the parent is adjacent on that side.
    if (this.attachedTo) {
      const parentFurniture = this.attachedTo.parentFurniture;
      if (parentFurniture?.furnitureData?.componentType === "module") {
        if (this.moduleConnectionSide === side &&
            requiredUrlCodes.includes(parentFurniture.furnitureData?.urlCode)) {
          return true;
        }
      }
    }

    // Case 2: this module is the parent — check if any child is connected on the specified side
    for (const child of this.attachedChildren) {
      if (child.furnitureData?.componentType !== "module") continue;
      if (!requiredUrlCodes.includes(child.furnitureData?.urlCode)) continue;
      // child.attachedTo.side is the side of THIS module (parent) that the child is connected to
      if (child.attachedTo?.side === side) return true;
    }

    // Case 3: geometric group-walk — handles indirect connections (e.g. E — R — this).
    // Cases 1 & 2 only reach direct neighbours; a required module two hops away (across
    // an R corner piece) is missed.
    return this._groupModuleOnSide(side, (other) => requiredUrlCodes.includes(other.furnitureData?.urlCode));
  },

  /**
   * Geometric group walk: whether another module of this group sits on `side` — its centre
   * within reach of both bounding extents (+0.5 m) and within ~45° of the side's world
   * direction. The direct parent is skipped: Case 1 of both callers already answers for it,
   * and a parent on a left/right AP with a non-zero Z offset would otherwise read as "back".
   */
  _groupModuleOnSide(side, accepts = null) {
    const localDir = LOCAL_SIDE_DIR[side];
    if (!localDir) return false;

    const modules = this.getAllGroupModules();
    if (modules.size <= 1) return false;

    const myPos = this.mesh.position;
    const worldDir = _adjWorldDir.copy(localDir).applyQuaternion(this.mesh.quaternion);
    // Reach scales with both pieces, so L-shaped or wide modules whose centres sit far apart still count.
    const myHalfExtent = this.boundingBox
      ? Math.max(this.boundingBox.size.x, this.boundingBox.size.z) / 2
      : 1.5;
    const directParent = this.attachedTo?.parentFurniture ?? null;

    for (const other of modules) {
      if (other === this || other === directParent) continue;
      if (accepts && !accepts(other)) continue;

      const toOther = _adjToOther.subVectors(other.mesh.position, myPos);
      toOther.y = 0;
      const distance = toOther.length();
      if (distance < 0.01) continue;

      const otherHalfExtent = other.boundingBox
        ? Math.max(other.boundingBox.size.x, other.boundingBox.size.z) / 2
        : 1.5;
      if (distance > myHalfExtent + otherHalfExtent + 0.5) continue;

      toOther.normalize();
      if (worldDir.dot(toOther) > 0.7) return true;
    }

    return false;
  },

  // Check if a panel can be attached to a specific attachment point
  canAttachPanel(attachmentPoint, componentType) {
    // Only apply restriction for panel components on base modules
    if (componentType !== "panel") return true;
    if (!this.furnitureData?.componentType || this.furnitureData.componentType !== "module") return true;

    const targetSide = attachmentPoint.side;

    // Modules that carry panels on every/opposing face at once (matacao3 corners, oiko armrests)
    // opt out of the opposite-side and adjacency exclusions; only the per-face slot count applies.
    if (this.furnitureData?.properties?.panelsAllSides) {
      const slotsOnSide = this.attachmentPoints.filter(ap =>
        ap.side === targetSide && ap.acceptsComponentTypes.includes("panel")
      ).length;
      const panelsOnSide = this.attachedChildren.filter(child =>
        child.furnitureData?.componentType === "panel" &&
        child.attachedTo?.side === targetSide
      ).length;
      return panelsOnSide < slotsOnSide;
    }

    const oppositeSide = OPPOSITE_SIDES[targetSide];

    // A side cap replaces the opposite/adjacency rules: `longPanelSides` (the long faces exclude each other, 3 sides), `maxPanelSides` (matacao3-st's U).
    const declaredLongSides = this.furnitureData?.properties?.longPanelSides;
    const longSides = declaredLongSides?.length ? new Set(declaredLongSides) : null;
    const sideCap = this.furnitureData?.properties?.maxPanelSides ?? (longSides ? 3 : null);

    if (sideCap) {
      // If a panel exists on one long side, block the opposite long side
      if (longSides?.has(targetSide)) {
        const oppositeLongSide = OPPOSITE_SIDES[targetSide];
        const panelsOnOppositeLong = this.attachedChildren.filter(child =>
          child.furnitureData?.componentType === "panel" &&
          child.attachedTo?.side === oppositeLongSide
        ).length;
        if (panelsOnOppositeLong > 0) return false;
      }

      const occupiedSides = new Set(
        this.attachedChildren
          .filter(child => child.furnitureData?.componentType === "panel")
          .map(child => child.attachedTo?.side)
          .filter(Boolean)
      );
      if (occupiedSides.size >= sideCap && !occupiedSides.has(targetSide)) {
        return false;
      }
    } else {
      // Check if the opposite side has any panels attached
      const panelsOnOppositeSide = this.attachedChildren.filter(child =>
        child.furnitureData?.componentType === "panel" &&
        child.attachedTo?.side === oppositeSide
      ).length;

      // If opposite side has panels, block this side
      if (panelsOnOppositeSide > 0) {
        return false;
      }
    }

    // Count how many panel attachment points exist on the target side
    const attachmentPointsOnSide = this.attachmentPoints.filter(ap =>
      ap.side === targetSide && ap.acceptsComponentTypes.includes("panel")
    ).length;

    // Count how many panels are already attached on this side
    const panelsOnThisSide = this.attachedChildren.filter(child =>
      child.furnitureData?.componentType === "panel" &&
      child.attachedTo?.side === targetSide
    ).length;

    // If this side is full, don't allow more panels on it
    if (panelsOnThisSide >= attachmentPointsOnSide) {
      return false;
    }

    // Count total panel attachment points to determine module type
    const totalPanelSlots = this.attachmentPoints.filter(ap =>
      ap.acceptsComponentTypes.includes("panel")
    ).length;

    // Get all attached panels with their sides
    const attachedPanels = this.attachedChildren
      .filter(child => child.furnitureData?.componentType === "panel")
      .map(child => ({
        furniture: child,
        side: child.attachedTo?.side
      }));

    // If no panels attached yet, allow any placement
    if (attachedPanels.length === 0) return true;

    // Adjacency and the 2-panel limit apply only to 4-slot modules without a side cap (not Cascais L's 6, not cascais-v/matacao3-sd).
    if (!sideCap && totalPanelSlots === STANDARD_MODULE_PANEL_SLOTS && attachmentPointsOnSide === 1 && panelsOnThisSide === 0) {
      // Standard module with single slot per side: enforce adjacency rule if we already have 2 panels
      if (attachedPanels.length >= 2) return false;

      if (attachedPanels.length === 1) {
        const existingSide = attachedPanels[0].side;
        const adjacentSides = {
          'left': ['front', 'back'],
          'right': ['front', 'back'],
          'front': ['left', 'right'],
          'back': ['left', 'right']
        };
        return adjacentSides[existingSide]?.includes(targetSide) || false;
      }
    }

    return true;
  },
};
